import { Clock, FileText, CalendarDays, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const IMAGES = {
  plazos: "https://customer-assets.emergentagent.com/job_ia-concesiones-chile/artifacts/88sy4qua_plazos.png",
  documentos: "https://customer-assets.emergentagent.com/job_ia-concesiones-chile/artifacts/3jdhuitm_documentos.png",
  agenda: "https://customer-assets.emergentagent.com/job_ia-concesiones-chile/artifacts/nt6120dl_orden%20del%20dia.png",
  ia: "https://customer-assets.emergentagent.com/job_ia-concesiones-chile/artifacts/kf85409k_IA.png",
};

const modules = [
  {
    icon: Clock,
    name: "Asistente de Plazos y Entregas",
    description:
      "Arranca por ti las tareas con fecha encima: analiza, prepara y te deja un borrador listo para revisar. Dejas de postergar y llegas a tiempo sin el desgaste.",
    available: true,
    badge: "Disponible ahora",
    image: IMAGES.plazos,
    category: "Plazos",
  },
  {
    icon: FileText,
    name: "Asistente de Revisión Documental",
    description:
      "Hace el trabajo repetitivo de documentar, ordenar y dar formato. Tú solo tomas la decisión final, sin gastar el día en revisar lo mismo una y otra vez.",
    available: true,
    badge: "Disponible ahora",
    image: IMAGES.documentos,
    category: "Documentos",
  },
  {
    icon: CalendarDays,
    name: "Asistente de Organización Diaria",
    description:
      "Ordena tu agenda, tus pendientes y tus recordatorios para que nada se te escape. Reaccionas a tiempo y recuperas la sensación de tener el día bajo control.",
    available: false,
    badge: "Próximamente",
    image: IMAGES.agenda,
    category: "Agenda",
  },
  {
    icon: Sparkles,
    name: "Tu Primer Paso con IA",
    description:
      "Si sientes que el tren de la IA se va sin ti, este es tu punto de partida. Te muestro, paso a paso y a tu medida, cómo empezar a usarla en lo que tú haces.",
    available: false,
    badge: "Próximamente",
    image: IMAGES.ia,
    category: "Inteligencia Artificial",
  },
];

export default function ModulesSection() {
  return (
    <section
      id="modulos"
      data-testid="modules-section"
      className="py-0 relative scroll-mt-20"
    >
      {/* Luminous background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#141820] to-[#111111]" />
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-[120px]"
          style={{ background: "radial-gradient(circle, #22D3EE, transparent)" }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15 blur-[100px]"
          style={{ background: "radial-gradient(circle, #FF6B9D, transparent)" }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 w-full">
        {/* Title */}
        <div className="text-center mb-8">
          <p className="text-sm sm:text-base uppercase tracking-[0.2em] font-bold text-cyan-400 mb-4">
            Módulos
          </p>
          <h2
            data-testid="modules-title"
            className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Tus agentes IA, listos para trabajar por ti
          </h2>
        </div>

        {/* Grid 2x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
          {modules.map((mod, i) => {
            const Icon = mod.icon;
            const isAvailable = mod.available;
            return (
              <div
                key={i}
                data-testid={`module-card-${i + 1}`}
                className={`group rounded-2xl overflow-hidden transition-all duration-300 ${
                  isAvailable
                    ? "border border-cyan-500/30 shadow-[0_0_20px_rgba(0,188,212,0.1)] bg-[#1a1f2e]/80 backdrop-blur-md"
                    : "border border-white/10 bg-[#1a1f2e]/50 backdrop-blur-md hover:border-white/20"
                }`}
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={mod.image}
                    alt={mod.name}
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                      !isAvailable ? "opacity-[0.85] saturate-[0.8]" : ""
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1f2e] via-transparent to-transparent opacity-80" />

                  {/* Category badge */}
                  <div className="absolute top-3 left-3">
                    <Badge
                      className={`backdrop-blur-md text-xs font-medium px-3 py-1 border-0 ${
                        isAvailable
                          ? "bg-black/60 text-cyan-400"
                          : "bg-black/60 text-white/60"
                      }`}
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      <Icon className="w-3 h-3 mr-1.5" />
                      {mod.category}
                    </Badge>
                  </div>

                  {/* Status badge */}
                  <div className="absolute top-3 right-3">
                    <Badge
                      data-testid={`module-badge-${i + 1}`}
                      className={`text-[10px] px-2.5 py-1 rounded-full border-0 backdrop-blur-md ${
                        isAvailable
                          ? "bg-black/60 text-cyan-400"
                          : "bg-black/60 text-white/50"
                      }`}
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {mod.badge}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3
                    className={`text-base font-medium mb-2 transition-colors ${
                      isAvailable
                        ? "text-white group-hover:text-cyan-400"
                        : "text-white/75"
                    }`}
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {mod.name}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      isAvailable ? "text-white/70" : "text-white/50"
                    }`}
                  >
                    {mod.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
